import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const ensureMetaTag = (property, content) => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', property); document.head.appendChild(tag); }
  tag.setAttribute('content', content);
};
const ensureNamedMeta = (name, content) => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', name); document.head.appendChild(tag); }
  tag.setAttribute('content', content);
};

const UploadPage = () => {
  const { token } = useParams();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [linkActive, setLinkActive] = useState(true);
  const [linkStatusChecked, setLinkStatusChecked] = useState(false);
  const [eventName, setEventName] = useState('');
  const [userName, setUserName] = useState('');
  const [bannerImage, setBannerImage] = useState(null);
  const [requiresPin, setRequiresPin] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [verifyingPin, setVerifyingPin] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [currentFileName, setCurrentFileName] = useState('');
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    const checkLinkStatus = async () => {
      if (!token) {
        setMessage('Error: Invalid upload link');
        setLinkActive(false);
        setLinkStatusChecked(true);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/upload/${token}/status`);
        setLinkActive(response.data.active);
        setEventName(response.data.event_name || '');
        setUserName(response.data.user_name || '');
        setRequiresPin(!!response.data.requiresPin);
        setBannerImage(response.data.banner_image || null);
        if (!response.data.active) setMessage(response.data.message || 'Upload link is deactivated.');
      } catch (error) {
        if (error.response?.status === 404) {
          setMessage('Invalid upload link. Please check the link and try again.');
          setLinkActive(false);
        } else {
          setLinkActive(true);
        }
      } finally {
        setLinkStatusChecked(true);
      }
    };
    checkLinkStatus();
  }, [token]);

  const handleVerifyPin = async (e) => {
    e.preventDefault();
    setPinError('');
    const pinStr = (pin || '').replace(/\D/g, '');
    if (pinStr.length !== 6) { setPinError('Enter a 6-digit PIN'); return; }
    setVerifyingPin(true);
    try {
      const response = await axios.post(`${API_URL}/upload/${token}/verify-pin`, { pin: pinStr });
      if (response.data.valid) { setPinVerified(true); setPin(pinStr); setPinError(''); }
      else setPinError(response.data.error || 'Invalid PIN');
    } catch (err) {
      setPinError(err.response?.data?.error || 'Invalid PIN');
    } finally {
      setVerifyingPin(false);
    }
  };

  useEffect(() => {
    const baseTitle = 'Upload Photos';
    const title = eventName ? `${eventName} • Upload Photos` : baseTitle;
    const description = eventName ? `Upload your event photos for ${eventName}` : 'Upload photos securely.';
    if (typeof document !== 'undefined') {
      document.title = title;
      ensureMetaTag('og:title', title);
      ensureMetaTag('og:description', description);
      ensureNamedMeta('description', description);
      ensureMetaTag('og:type', 'website');
      ensureMetaTag('og:url', window.location.href);
    }
    return () => { if (typeof document !== 'undefined') document.title = 'AI Pix'; };
  }, [eventName]);

  const validateAndUpload = async (fileListInput) => {
    const fileArray = Array.from(fileListInput);
    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'];
    const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

    if (fileArray.length === 0) { setMessage('Please select at least one photo'); return; }

    const invalidFiles = [];
    const oversizedFiles = [];

    fileArray.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) { oversizedFiles.push(file.name); return; }
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!ALLOWED_EXTENSIONS.includes(fileExtension)) { invalidFiles.push(file.name); return; }
      if (!ALLOWED_MIME_TYPES.includes(file.type)) { invalidFiles.push(file.name); return; }
    });

    if (invalidFiles.length > 0) {
      setMessage(`${invalidFiles.length} file(s) are not valid images. Only JPG, PNG, GIF allowed.`);
      return;
    }
    if (oversizedFiles.length > 0) {
      setMessage(`${oversizedFiles.length} file(s) exceed the 50 MB limit.`);
      return;
    }

    setMessage('');
    setUploading(true);
    setUploadProgress(0);
    setTotalFiles(fileArray.length);
    setFileList(fileArray.map((f) => ({ name: f.name, status: 'pending', size: f.size })));

    let batchId = null;
    let batchNumber = null;
    let successCount = 0;
    let lastError = null;

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      setCurrentFileName(file.name);
      setCurrentFileIndex(i + 1);
      setUploadProgress(0);
      setFileList((prev) => { const next = [...prev]; next[i] = { ...next[i], status: 'uploading' }; return next; });

      const formData = new FormData();
      formData.append('photos', file);
      if (requiresPin && pin) formData.append('pin', pin);
      if (batchId != null && i > 0) formData.append('batch_id', batchId);

      try {
        const response = await axios.post(`${API_URL}/upload/${token}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && progressEvent.total > 0) {
              setUploadProgress(Math.min(100, Math.max(0, Math.round((progressEvent.loaded * 100) / progressEvent.total))));
            }
          },
        });
        if (response.data.batch_id != null) batchId = response.data.batch_id;
        if (response.data.batch_number != null) batchNumber = response.data.batch_number;
        successCount += response.data.photos_count ?? 1;
        setFileList((prev) => { const next = [...prev]; next[i] = { ...next[i], status: 'done' }; return next; });
      } catch (error) {
        let errorMessage = 'Upload failed';
        if (error.response) errorMessage = error.response.data?.error || `Server error: ${error.response.status}`;
        else if (error.code === 'ECONNABORTED') errorMessage = 'Upload timed out. Try again with fewer photos.';
        else errorMessage = 'Network error. Check your connection and try again.';
        lastError = errorMessage;
        setFileList((prev) => { const next = [...prev]; next[i] = { ...next[i], status: 'error' }; return next; });
        setMessage(`Error uploading "${file.name}": ${errorMessage}`);
        break;
      }
    }

    setUploading(false);
    setCurrentFileName('');
    setCurrentFileIndex(0);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (lastError == null && successCount > 0) {
      const batchNum = batchNumber != null ? batchNumber : '';
      setMessage(`success:${successCount} photo${successCount !== 1 ? 's' : ''} uploaded successfully!${batchNum ? ` (Batch #${batchNum})` : ''}`);
    } else if (successCount > 0 && lastError) {
      setMessage((m) => `${m} · ${successCount} photo${successCount !== 1 ? 's' : ''} uploaded before error.`);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) validateAndUpload(e.target.files);
  };
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (e.dataTransfer.files?.length > 0) validateAndUpload(e.dataTransfer.files);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isSuccess = typeof message === 'string' && message.startsWith('success:');
  const displayMessage = isSuccess ? message.replace('success:', '') : message;

  /* ── Loading ── */
  if (!linkStatusChecked) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto">
            <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-slate-500 text-sm">Checking link…</p>
        </div>
      </div>
    );
  }

  /* ── Deactivated ── */
  if (!linkActive) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm max-w-sm w-full p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-rose-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Link Deactivated</h1>
          <p className="text-slate-500 text-sm">
            {message || 'This upload link has been deactivated. Please contact the event organiser.'}
          </p>
        </div>
      </div>
    );
  }

  /* ── PIN gate ── */
  if (requiresPin && !pinVerified) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          {/* Brand */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 shadow-md"
              style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)' }}>
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-slate-900">{eventName || 'Upload Photos'}</h1>
            <p className="text-slate-500 text-sm mt-1">Enter the 6-digit PIN to continue</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <form onSubmit={handleVerifyPin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Event PIN</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white text-center text-2xl font-bold tracking-[0.5em] focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 transition-all"
                  placeholder="······"
                  autoFocus
                />
              </div>
              {pinError && (
                <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl">
                  <svg className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-rose-700 text-sm">{pinError}</p>
                </div>
              )}
              <button
                type="submit"
                disabled={verifyingPin || pin.replace(/\D/g, '').length !== 6}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
              >
                {verifyingPin ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying…
                  </>
                ) : 'Continue →'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  /* ── Main Upload ── */
  const overallPercent = totalFiles > 0
    ? Math.min(100, (((currentFileIndex - 1) + Math.min(100, Math.max(0, uploadProgress)) / 100) / totalFiles) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)' }}>
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 truncate">{eventName || 'Upload Photos'}</p>
            {userName && <p className="text-xs text-slate-400 truncate">Organised by {userName}</p>}
          </div>
        </div>
      </div>

      {/* Banner */}
      {bannerImage && (
        <div className="max-w-2xl mx-auto">
          <img
            src={bannerImage}
            alt={eventName || 'Event banner'}
            className="w-full object-cover"
            style={{ maxHeight: '220px' }}
          />
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        {/* Drop zone */}
        {!uploading && (
          <div
            ref={dropZoneRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all select-none ${
              isDragging
                ? 'border-violet-500 bg-violet-50 scale-[1.01]'
                : 'border-slate-300 bg-white hover:border-violet-400 hover:bg-violet-50/30'
            }`}
          >
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-colors ${isDragging ? 'bg-violet-100' : 'bg-slate-100'}`}>
              <svg className={`w-8 h-8 transition-colors ${isDragging ? 'text-violet-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-base font-semibold text-slate-700 mb-1">
              {isDragging ? 'Drop photos here' : 'Drop photos here or tap to browse'}
            </p>
            <p className="text-sm text-slate-400">JPG, PNG, GIF · up to 50 MB per file</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
            />
          </div>
        )}

        {/* Upload progress */}
        {uploading && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {currentFileName
                    ? (uploadProgress >= 100 ? `Saving ${currentFileName}` : `Uploading ${currentFileName}`)
                    : 'Uploading…'}
                </p>
              </div>
              <span className="text-xs font-bold text-violet-600 flex-shrink-0 tabular-nums">
                {currentFileIndex}/{totalFiles} · {Math.round(overallPercent)}%
              </span>
            </div>

            {/* Overall progress bar */}
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{ width: `${overallPercent}%`, background: 'linear-gradient(90deg, #7c3aed, #4f46e5)' }}
              />
            </div>

            {/* File list */}
            {fileList.length > 0 && (
              <ul className="space-y-1.5 max-h-52 overflow-y-auto">
                {fileList.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2.5 text-sm py-1 border-b border-slate-50 last:border-0">
                    {item.status === 'done' && (
                      <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      </span>
                    )}
                    {item.status === 'uploading' && (
                      <span className="flex-shrink-0">
                        <svg className="w-4 h-4 text-violet-600 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      </span>
                    )}
                    {item.status === 'error' && (
                      <span className="w-4 h-4 rounded-full bg-rose-500 flex items-center justify-center flex-shrink-0">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
                      </span>
                    )}
                    {item.status === 'pending' && (
                      <span className="w-4 h-4 rounded-full border-2 border-slate-200 flex-shrink-0" />
                    )}
                    <span className={`truncate flex-1 ${item.status === 'error' ? 'text-rose-700' : item.status === 'done' ? 'text-slate-400' : 'text-slate-700'}`}>
                      {item.name}
                    </span>
                    {item.size != null && (
                      <span className="text-xs text-slate-300 flex-shrink-0">{formatFileSize(item.size)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Success / Error message */}
        {displayMessage && (
          <div className={`flex items-start gap-3 p-4 rounded-2xl border ${
            isSuccess
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-rose-50 border-rose-200'
          }`}>
            {isSuccess ? (
              <div className="w-8 h-8 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
              </div>
            ) : (
              <div className="w-8 h-8 rounded-xl bg-rose-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
            )}
            <div>
              <p className={`text-sm font-semibold ${isSuccess ? 'text-emerald-800' : 'text-rose-800'}`}>
                {isSuccess ? 'Upload complete!' : 'Upload failed'}
              </p>
              <p className={`text-sm mt-0.5 ${isSuccess ? 'text-emerald-700' : 'text-rose-700'}`}>
                {displayMessage}
              </p>
            </div>
          </div>
        )}

        {/* Upload more button after success */}
        {isSuccess && !uploading && (
          <button
            type="button"
            onClick={() => { setMessage(''); setFileList([]); }}
            className="w-full py-3 rounded-2xl border-2 border-dashed border-violet-300 text-violet-600 text-sm font-semibold hover:bg-violet-50 transition-colors"
          >
            + Upload more photos
          </button>
        )}

        <p className="text-center text-xs text-slate-300 pt-2">Powered by AI face recognition</p>
      </div>
    </div>
  );
};

export default UploadPage;
